import { useEffect, useReducer, useState } from "react";

import {
  GEO_COORDINATES_FROM_BROWSER,
  GEO_COORDINATES_FROM_SEARCH,
  SEARCH_FORM_GENERAL,
  FALLBACK_COORDINATES,
} from "@constants";

import { fetchJSON, errorLogger, getLocalStorageCoordinates } from "@utilities";

import { SearchListLocalization } from "js/components/widgets/SearchList/types";
import { getReducerInitialState } from "./utils";
import reducer from "./store/reducer";
import {
  setIsLoadingAction,
  setIsLoadingMoreAction,
  setHasErrorAction,
  setErrorMessageAction,
  setLoadMoreResponseAction,
  setLoadNewResponseAction,
} from "./store/actions";

interface ListApiResponseJSON<TItem> extends GenericApiResponseJSON {
  payload: {
    data: TItem[];
    pagination: Pagination;
    localization: SearchListLocalization;
  };
}

interface Props<TItem> {
  api?: string;
  apiQueryKey?: string;
  defaultQuery?: string;
  fetchBodyData?: Record<string, unknown>;
  formElement?: HTMLFormElement;
  formInputName?: string;
  defaultItems?: TItem[];
  defaultPagination?: Pagination;
  cultureCode?: string;
  defaultErrorMessage?: string;
  pageSize?: number;
  pageSizeDetail?: number;
  hasGeolocation?: boolean;
  hasSearchCoordinates?: boolean;
  latitude?: number;
  longitude?: number;
  currency?: string;
  date?: string;
  searchFolder?: string;
  hideSegmentBreadcrumb?: boolean;
}

interface Output<TItem> {
  items: TItem[];
  hasNextItems: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  query: string;
  setQuery: (newQuery: string) => void;
  loadNewItemsWithQuery: (query: string) => Promise<void>;
  loadNextItems: () => void;
  loadNewItemsWithFallbackCoordinates: () => Promise<void>;
  loadMoreItemsWithFallbackCoordinates: () => Promise<void>;
  loadNewItemsWithSearchCoordinates: () => Promise<void>;
  loadMoreItemsWithSearchCoordinates: () => Promise<void>;
  loadNewItemsWithBrowserCoordinates: () => Promise<void>;
  loadMoreItemsWithBrowserCoordinates: () => Promise<void>;
  loadNewItemsOnDetailPage: () => Promise<void>;
  loadMoreItemsOnDetailPage: () => Promise<void>;
  loadMoreItemsExchangeRatesDetail: () => Promise<void>;
  hasError: boolean;
  errorMessage: string;
  localization?: SearchListLocalization;
}

const LOAD_3_ITEMS = 3;
const LOAD_10_ITEMS = 10;

const usePagination = <TItem>({
  api,
  apiQueryKey = "query",
  defaultQuery,
  fetchBodyData,
  formElement,
  formInputName,
  defaultItems,
  defaultPagination,
  cultureCode,
  defaultErrorMessage,
  pageSize,
  pageSizeDetail,
  hasGeolocation,
  hasSearchCoordinates = false,
  latitude,
  longitude,
  currency,
  date,
  searchFolder,
  hideSegmentBreadcrumb,
}: Props<TItem>): Output<TItem> => {
  const [
    { hasNextItems, isLoading, isLoadingMore, items, pagination, hasError, errorMessage },
    dispatch,
  ] = useReducer(
    reducer,
    getReducerInitialState({
      items: defaultItems,
      pagination: defaultPagination,
      errorMessage: defaultErrorMessage || "",
    })
  );
  const [query, setQuery] = useState(defaultQuery);
  const [localization, setLocalization] = useState<SearchListLocalization>(undefined);

  // FORM STATES - success, error
  const handleLoadMoreSuccess = ({ payload }: ListApiResponseJSON<TItem>): void => {
    if (payload) {
      dispatch(setLoadMoreResponseAction(payload));
    }
  };

  const handleLoadNewItemsSuccess = ({ payload }: ListApiResponseJSON<TItem>): void => {
    if (payload) {
      dispatch(setLoadNewResponseAction(payload));
    }
  };

  const handleError = (error: FetchError): void => {
    dispatch(setHasErrorAction(true));

    if (error?.data?.message) {
      dispatch(setErrorMessageAction(error.data.message));

      errorLogger(error.data.message);
    }
  };

  // FORM with QUERY (general search form) - 'search' input
  const loadNewItemsWithQuery = async (queryValue: string): Promise<void> => {
    //if (!queryValue) {
    //  return;
    //}

    dispatch(setIsLoadingAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            [apiQueryKey]: queryValue,
            limit: pageSize ?? 9,
            offset: 0,
            currentPage: 1,
            cultureCode,
            searchFolder,
            hideSegmentBreadcrumb,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      setLocalization(response.payload.localization);
      handleLoadNewItemsSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingAction(false));
    }
  };

  // FORM with QUERY (general search form) - 'load more' button
  const loadNextItems = async (): Promise<void> => {
    if (!hasNextItems || isLoading || isLoadingMore) return;

    dispatch(setIsLoadingMoreAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            [apiQueryKey]: query,
            limit: pagination.rowsOnPage,
            offset: items.length,
            cultureCode,
            searchFolder,
            hideSegmentBreadcrumb,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      handleLoadMoreSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingMoreAction(false));
    }
  };

  // LOAD NEW ITEMS (with 'geo location' FALLBACK coordinates)
  const loadNewItemsWithFallbackCoordinates = async (): Promise<void> => {
    dispatch(setIsLoadingAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            limit: hasGeolocation ? pageSizeDetail ?? LOAD_3_ITEMS : pageSize ?? LOAD_10_ITEMS,
            offset: 0,
            cultureCode,
            latitude: FALLBACK_COORDINATES.lat,
            longitude: FALLBACK_COORDINATES.lng,
            isPageDetail: hasGeolocation,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      setLocalization(response.payload.localization);
      handleLoadNewItemsSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingAction(false));
    }
  };

  // LOAD MORE ITEMS - 'load more' button (with 'geo location' FALLBACK coordinates)
  const loadMoreItemsWithFallbackCoordinates = async (): Promise<void> => {
    if (!hasNextItems || isLoading || isLoadingMore) return;

    dispatch(setIsLoadingMoreAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            limit: hasGeolocation ? LOAD_10_ITEMS : pagination.rowsOnPage,
            offset: items.length,
            cultureCode,
            latitude: FALLBACK_COORDINATES.lat,
            longitude: FALLBACK_COORDINATES.lng,
            isPageDetail: hasGeolocation,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      handleLoadMoreSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingMoreAction(false));
    }
  };

  // FORM with COORDINATES - 'search' input (does not use input value as query but 'geo location' coordinates)
  const loadNewItemsWithSearchCoordinates = async (): Promise<void> => {
    dispatch(setIsLoadingAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            limit: hasGeolocation ? pageSizeDetail ?? LOAD_3_ITEMS : pageSize ?? LOAD_10_ITEMS,
            offset: 0,
            cultureCode,
            latitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH).lat,
            longitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH).lng,
            isPageDetail: hasGeolocation,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      setLocalization(response.payload.localization);
      handleLoadNewItemsSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingAction(false));
    }
  };

  // FORM with COORDINATES - 'load more' button (does not use input value as query but 'geo location' coordinates)
  const loadMoreItemsWithSearchCoordinates = async (): Promise<void> => {
    if (!hasNextItems || isLoading || isLoadingMore) return;

    dispatch(setIsLoadingMoreAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            limit: hasGeolocation ? LOAD_10_ITEMS : pagination.rowsOnPage,
            offset: items.length,
            cultureCode,
            latitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH).lat,
            longitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH).lng,
            isPageDetail: hasGeolocation,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      handleLoadMoreSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingMoreAction(false));
    }
  };

  // Uses browser Geolocation API - load 'new' items
  const loadNewItemsWithBrowserCoordinates = async (): Promise<void> => {
    dispatch(setIsLoadingAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            limit: hasGeolocation ? pageSizeDetail ?? LOAD_3_ITEMS : pageSize ?? LOAD_10_ITEMS,
            offset: 0,
            cultureCode,
            latitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_BROWSER).lat,
            longitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_BROWSER).lng,
            isPageDetail: hasGeolocation,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      setLocalization(response.payload.localization);
      handleLoadNewItemsSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingAction(false));
    }
  };

  // Uses browser Geolocation API - load 'more' items
  const loadMoreItemsWithBrowserCoordinates = async (): Promise<void> => {
    if (!hasNextItems || isLoading || isLoadingMore) return;

    dispatch(setIsLoadingMoreAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            limit: hasGeolocation ? LOAD_10_ITEMS : pagination.rowsOnPage,
            offset: items.length,
            cultureCode,
            latitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_BROWSER).lat,
            longitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_BROWSER).lng,
            isPageDetail: hasGeolocation,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      handleLoadMoreSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingMoreAction(false));
    }
  };

  // On Detail page uses known latitude & longitude = loads 'new' items
  const loadNewItemsOnDetailPage = async (): Promise<void> => {
    dispatch(setIsLoadingAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            limit: pageSizeDetail ?? LOAD_3_ITEMS,
            offset: 0,
            cultureCode,
            latitude,
            longitude,
            isPageDetail: hasGeolocation,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      setLocalization(response.payload.localization);
      handleLoadNewItemsSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingAction(false));
    }
  };

  // On Detail page uses known latitude & longitude = loads 'more' items
  const loadMoreItemsOnDetailPage = async (): Promise<void> => {
    if (!hasNextItems || isLoading || isLoadingMore) return;

    dispatch(setIsLoadingMoreAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            limit: LOAD_10_ITEMS,
            offset: items.length,
            cultureCode,
            latitude,
            longitude,
            isPageDetail: hasGeolocation,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      handleLoadMoreSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingMoreAction(false));
    }
  };

  // ExchangeRatesDetail = loads 'more' items
  const loadMoreItemsExchangeRatesDetail = async (): Promise<void> => {
    if (!hasNextItems || isLoading || isLoadingMore) return;

    dispatch(setIsLoadingMoreAction(true));

    try {
      const response = (await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            currency,
            date,
            cultureCode,
            limit: pagination.rowsOnPage,
            offset: items.length,
            initPageSize: items.length,
            ...fetchBodyData,
          }),
        },
      })) as ListApiResponseJSON<TItem>;

      handleLoadMoreSuccess(response);
    } catch (error) {
      handleError(error as FetchError);
    } finally {
      dispatch(setIsLoadingMoreAction(false));
    }
  };

  // HANDLE FORM SUBMIT
  const handleFormSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();

    if (isLoading) return;

    const formInput: HTMLInputElement = event.target[formInputName];

    if (!formInput) {
      console.error(`No '${formInputName}' input in form found`);
      return;
    }

    const inputValue = formInput.value.trim();
    setQuery(inputValue);

    if (formElement.classList.contains(SEARCH_FORM_GENERAL)) {
      window.history.replaceState(null, "", `?query=${inputValue}`);
      // const persoElement = document.querySelector(".js-search-icon") as HTMLElement;
      // if (persoElement.dataset.persoo) {
      //   return;
      // }
    }
    if (hasSearchCoordinates) {
      loadNewItemsWithSearchCoordinates();
    } else {
      loadNewItemsWithQuery(inputValue);
    }
  };

  useEffect(() => {
    // Return undefined because of consistent return error
    if (!formElement || !formInputName) return undefined;

    formElement.addEventListener("submit", handleFormSubmit);

    return () => {
      formElement.removeEventListener("submit", handleFormSubmit);
    };
  }, [formElement]);

  return {
    hasNextItems,
    isLoading,
    isLoadingMore,
    items: items as TItem[],
    query,
    setQuery,
    loadNewItemsWithQuery,
    loadNextItems,
    loadNewItemsWithFallbackCoordinates,
    loadMoreItemsWithFallbackCoordinates,
    loadNewItemsWithSearchCoordinates,
    loadMoreItemsWithSearchCoordinates,
    loadNewItemsWithBrowserCoordinates,
    loadMoreItemsWithBrowserCoordinates,
    loadNewItemsOnDetailPage,
    loadMoreItemsOnDetailPage,
    loadMoreItemsExchangeRatesDetail,
    hasError,
    errorMessage,
    localization,
  };
};

export default usePagination;
